import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { SectionLabel } from "@/components/yanre/SectionLabel";
import { Reveal, RevealLines } from "@/components/yanre/Reveal";
import { track, attribution, metaClickIds, fireProjectReceived } from "@/lib/track";
import { readConsent } from "@/lib/consent";
import { useLocale } from "@/i18n/LocaleContext";
import { MARKETS } from "@/i18n";
import { COUNTRIES, LOCATION_STATUS, FACILITY_AREAS, CONTACT_METHODS } from "./data";
import { PhoneIntl, isValidPhoneNumber } from "./PhoneIntl";
import { FormIntro } from "./FormIntro";

const API = process.env.REACT_APP_BACKEND_URL;

export const Option = ({ selected, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`flex w-full items-center justify-between gap-3 border px-4 py-3.5 text-left font-mono text-[11px] uppercase tracking-[0.1em] transition-colors duration-200 rtl:text-right sm:text-xs ${
      selected
        ? "border-signal bg-signal/10 text-white"
        : "border-carbon-600 text-titanium/70 hover:border-titanium/50 hover:text-titanium"
    }`}
  >
    {children}
    {selected && <Check className="h-4 w-4 shrink-0 text-signal" />}
  </button>
);

export const Input = ({ label, required, ...props }) => (
  <label className="block">
    <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-titanium/50">
      {label} {required && <span className="text-signal">*</span>}
    </span>
    <input
      {...props}
      className="w-full rounded-none border border-carbon-600 bg-carbon-800 px-4 py-3.5 text-sm text-white outline-none transition-colors placeholder:text-titanium/30 focus:border-signal"
    />
  </label>
);

export const Question = ({ label, required, hint, children }) => (
  <div className="mb-8">
    <p className="mb-1 font-display text-xl font-bold uppercase text-white sm:text-2xl">
      {label} {required && <span className="text-signal">*</span>}
    </p>
    {hint && <p className="mb-4 text-sm font-light text-titanium/50">{hint}</p>}
    {!hint && <div className="mb-4" />}
    <div className="space-y-2">{children}</div>
  </div>
);

// 4-STEP PROJECT BUILDER (conversion-optimized, approved 2026-07).
// Answer VALUES stay canonical English and map onto existing HubSpot options;
// only labels translate. Removed qualification questions keep their CRM
// properties untouched for later commercial qualification.
// [canonical value, translation key, English default]
const PROJECT_TYPES_V2 = [
  ["commercial-gym", "opt.commercial-gym", "Complete Gym Project"],
  ["upgrade-expand", "opt.upgrade-expand", "Upgrade / Expand an Existing Gym"],
  ["pt-studio", "opt.pt-studio", "Personal Training Studio"],
  ["residential-gym", "opt.residential-gym", "Condominium / Residential Gym"],
  ["hotel-resort", "opt.hotel-resort", "Hotel / Resort Fitness"],
  ["corporate-gym", "opt.corporate-gym", "Corporate Gym"],
  ["franchise", "opt.franchise", "Gym Franchise"],
];
const TIMELINES_V2 = [
  ["1-2", "pts.opt.time.1-2", "1–2 months"],
  ["2-4", "pts.opt.time.2-4", "2–4 months"],
  ["4-6", "pts.opt.time.4-6", "4–6 months"],
  ["6-plus", "pts.opt.time.6-plus", "More than 6 months"],
];
// Investment: existing HubSpot internal values; universal USD labels in
// every language and market (USD written literally, numbers universal).
const INVESTMENTS_V2 = [
  ["up-to-50k", "opt.up-to-50k", "Under USD 50,000"],
  ["50-150k", "opt.50-150k", "USD 50,000–150,000"],
  ["150-300k", "opt.150-300k", "USD 150,000–300,000"],
  ["300-500k", "opt.300-500k", "USD 300,000–500,000"],
  ["500k-1m", "opt.500k-1m", "USD 500,000–1M"],
  ["1m-plus", "opt.1m-plus", "Over USD 1M"],
];
const EQUIP_LIST_V2 = [
  ["have-list", "opt.have-list", "Yes — I already have an equipment list"],
  ["need-review", "opt.need-review", "Partially — I have an initial selection"],
  ["need-list", "opt.need-list", "No — I need YANRE's help"],
];

const STEP_NAMES = ["Project", "Space", "Investment", "Contact"];
const TOTAL = 4;

// English fallback copy for validation messages (localized via err.* keys).
const ERR_DEFAULTS = {
  "err.projectType": "Please select your project type.",
  "err.openingTimeline": "Please select when you plan to open your project.",
  "err.locationStatus": "Please select your location status.",
  "err.facilityArea": "Please select the approximate floor area.",
  "err.investmentBand": "Please select your estimated investment range.",
  "err.equipmentListStatus": "Please tell us whether you have an equipment list.",
  "err.country": "Please select the project country.",
  "err.city": "Please enter the city.",
  "err.firstName": "Please enter your full name.",
  "err.email": "Please enter a valid email address.",
  "err.phone": "Please enter your phone / WhatsApp number.",
  "err.phoneInvalid": "Please enter a valid phone number for the selected country.",
  "err.preferredContact": "Please select your preferred contact method.",
  "err.privacyConsent": "Please accept the data-processing consent to continue.",
  "err.submit": "Something went wrong while sending. Please try again.",
};

// Phone country suggestion per locale (fallback: Portugal). The visitor can
// always change the phone country independently of the project country.
const LOCALE_PHONE_DEFAULT = { fr: "FR", de: "DE" };

const EMPTY = {
  projectType: "", openingTimeline: "",
  locationStatus: "", facilityArea: "",
  investmentBand: "", equipmentListStatus: "",
  country: "", city: "", fullName: "", company: "",
  email: "", phone: "", phoneCountry: "PT", phoneTouched: false, preferredContact: "",
  privacyConsent: false, marketingConsent: false,
};

// Questionnaire progress survives a language switch (same SPA session).
let savedForm = null;

// ONE canonical Project Builder per landing page. Anchor: #project-builder.
export const ProjectBuilderForm = ({ index }) => {
  const { locale, lang, t } = useLocale();
  // Africa Wave 1 country market (e.g. /ma → Morocco) — pins the project
  // country preselect and phone default; both stay visitor-editable.
  const market = MARKETS[locale] || null;
  // Locale-based phone country suggestion (France +33, Germany +49,
  // market routes +27/+212/+20/+213/+230/+254/+244/+267, …); all other
  // locales keep the existing default. Always independently editable.
  const initialPhoneCountry = market?.phone || LOCALE_PHONE_DEFAULT[locale] || "PT";
  const [step, setStep] = useState(savedForm?.step || 1);
  const [a, setA] = useState(() => savedForm?.answers || { ...EMPTY, phoneCountry: initialPhoneCountry, country: market?.country || "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [conversionId, setConversionId] = useState(null);
  const startedRef = useRef(Boolean(savedForm));

  useEffect(() => {
    track("form_step_view", { form: "project-builder", step, totalSteps: TOTAL });
  }, [step]);

  // Persist after every committed change — language switches restore from here.
  useEffect(() => {
    savedForm = { step, answers: a };
  }, [step, a]);

  const set = (k, v) => {
    if (!startedRef.current) {
      startedRef.current = true;
      track("form_start", { form: "project-builder" });
    }
    setA((p) => ({ ...p, [k]: v }));
  };

  const validate = () => {
    const req = (cond, msgKey) => {
      if (!cond) { toast.error(t(msgKey, ERR_DEFAULTS[msgKey])); return false; }
      return true;
    };
    switch (step) {
      case 1:
        return req(a.projectType, "err.projectType") && req(a.openingTimeline, "err.openingTimeline");
      case 2:
        return req(a.locationStatus, "err.locationStatus") && req(a.facilityArea, "err.facilityArea");
      case 3:
        return req(a.investmentBand, "err.investmentBand") && req(a.equipmentListStatus, "err.equipmentListStatus");
      case 4:
        return req(a.country, "err.country")
          && req(a.city.trim(), "err.city")
          && req(a.fullName.trim(), "err.firstName")
          && req(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(a.email), "err.email")
          && req(a.phone.trim(), "err.phone")
          && req(isValidPhoneNumber(a.phone), "err.phoneInvalid")
          && req(a.preferredContact, "err.preferredContact")
          && req(a.privacyConsent, "err.privacyConsent");
      default: return true;
    }
  };

  const next = () => {
    if (!validate()) return;
    track("form_step_complete", { form: "project-builder", step, stepName: t(`step.${step}`, STEP_NAMES[step - 1]) });
    setStep((s) => Math.min(s + 1, TOTAL));
  };
  const back = () => setStep((s) => Math.max(s - 1, 1));

  const submit = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      // Full Name → firstName / lastName (backend model needs both)
      const parts = a.fullName.trim().split(/\s+/);
      const firstName = parts[0];
      const lastName = parts.slice(1).join(" ");
      const payload = {
        projectType: a.projectType,
        locationStatus: a.locationStatus, facilityArea: a.facilityArea,
        openingTimeline: a.openingTimeline,
        investmentBand: a.investmentBand, equipmentListStatus: a.equipmentListStatus,
        // Removed-from-form qualification fields: not collected here anymore.
        // Empty values are dropped before HubSpot; CRM properties stay intact.
        projectNeed: [], decisionRole: "",
        country: a.country, city: a.city, firstName, lastName,
        company: a.company || null, email: a.email,
        phone: a.phone, preferredContact: a.preferredContact,
        privacyConsent: a.privacyConsent, marketingConsent: a.marketingConsent,
        // locale is the route (language locale OR country market, e.g. "ma");
        // canonical values stay language-independent
        locale,
        pageUrl: window.location.href,
        consentAdvertising: !!readConsent()?.advertising,
        ...metaClickIds(),
        // Attribution — persisted first-touch values (UTMs, click IDs,
        // campaign variant, search intent) survive every form step.
        ...attribution(),
        // CRM language LAST so attribution()'s path-derived value never
        // overrides it: country markets submit their resolved interface
        // language (Morocco/Algeria → "fr", Angola → "pt", others → "en")
        // while the Project Country field carries the country identity.
        language: lang,
      };
      const res = await fetch(`${API}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("submit failed");
      const data = await res.json();
      track("form_submit", { form: "project-builder", leadId: data.leadId });
      savedForm = null;
      // Conversion gate (2026-09 audit): project_received may fire ONLY for a
      // genuine backend-persisted submission (persisted:true + immutable
      // submissionId). A mocked/bare HTTP 200 shows the success UI but can
      // NEVER fire the Google Ads conversion.
      const confirmed = data && data.persisted === true && typeof data.submissionId === "string" && data.submissionId.length > 0;
      setConversionId(confirmed ? data.submissionId : null);
      setDone(data.leadId || data.submissionId || "received");
    } catch {
      toast.error(t("err.submit"));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (conversionId) fireProjectReceived(conversionId, { form: "project-builder" });
  }, [conversionId]);

  if (done) {
    return (
      <section id="project-builder" className="section-darker relative border-t border-carbon-700" data-testid="form-success">
        <div className="grid-lines absolute inset-0 opacity-40" />
        <div className="container-edge relative py-24 lg:py-36">
          <div className="mx-auto max-w-2xl border border-carbon-700 bg-carbon-900 p-10 text-center sm:p-14">
            <span className="mx-auto flex h-14 w-14 items-center justify-center bg-signal">
              <Check className="h-7 w-7 text-white" />
            </span>
            <h2 className="mt-8 font-display text-4xl font-extrabold uppercase leading-[0.92] text-white sm:text-5xl">
              {t("success.title1", "Project received")}<span className="text-signal">.</span>
            </h2>
            <p className="mt-6 text-base font-light leading-relaxed text-titanium/70">
              {t("success.body", "Thank you. A YANRE FITNESS EUROPE specialist will review your project and contact you through your preferred channel.")}
            </p>
            <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-titanium/40">
              {t("success.reference", "Reference")} · {done.slice(0, 8).toUpperCase()}
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="project-builder" className="section-darker relative overflow-hidden border-t border-carbon-700" data-testid="project-builder-form">
      <div className="grid-lines absolute inset-0 opacity-40" />
      <div className="container-edge relative py-16 lg:py-24">
        <div className="mx-auto max-w-3xl">
          <Reveal>
            <SectionLabel index={index}>Start Your Project</SectionLabel>
          </Reveal>
          <h2 className="mt-8 font-display text-4xl font-extrabold uppercase leading-[0.92] tracking-tight text-white sm:text-5xl lg:text-6xl">
            <RevealLines
              lines={[
                t("form.title1", "Build your gym"),
                <span key="p">
                  {t("form.title2", "project")}<span className="text-signal">.</span>
                </span>,
              ]}
            />
          </h2>
          <Reveal delay={0.15}>
            <FormIntro
              testidPrefix="form"
              sub1={t("form.intro.sub1", "4 QUICK STEPS.")}
              sub2={t("form.intro.sub2", "ONE COMPLETE GYM SOLUTION.")}
              support={t("form.intro.support", "Tell us about your project in just 4 simple steps. Our team will understand your needs and help you build the right solution — with direct factory supply from YANRE FITNESS China, professional consultation and after-sales support.")}
              steps={[1, 2, 3, 4].map((n) => t(`step.${n}`, STEP_NAMES[n - 1]))}
              minute={t("form.intro.minute", "1 MINUTE TO COMPLETE")}
            />
          </Reveal>

          <div className="mt-12 border border-carbon-700 bg-carbon-900">
            {/* Progress */}
            <div className="border-b border-carbon-700 px-6 py-5 sm:px-10">
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-titanium/50" data-testid="form-progress-label">
                  {t("form.progress", "Step")} {String(step).padStart(2, "0")} / {TOTAL}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-signal" data-testid="form-step-name">
                  {t(`step.${step}`, STEP_NAMES[step - 1])}
                </span>
              </div>
              <div className="mt-3 h-px w-full bg-carbon-700">
                <div className="h-px bg-signal transition-all duration-500" style={{ width: `${(step / TOTAL) * 100}%` }} data-testid="form-progress-bar" />
              </div>
            </div>

            <div className="px-6 py-8 sm:px-10 sm:py-10">
              {step === 1 && (
                <>
                  <Question label={t("q.projectType", "What best describes your project?")} required>
                    {PROJECT_TYPES_V2.map(([v, k, d]) => (
                      <Option key={v} testId={`f-project-type-${v}`} selected={a.projectType === v} onClick={() => set("projectType", v)}>{t(k, d)}</Option>
                    ))}
                  </Question>
                  <Question label={t("q.openingTimeline", "When do you plan to open your project?")} required>
                    {TIMELINES_V2.map(([v, k, d]) => (
                      <Option key={v} testId={`f-timeline-${v}`} selected={a.openingTimeline === v} onClick={() => set("openingTimeline", v)}>{t(k, d)}</Option>
                    ))}
                  </Question>
                </>
              )}

              {step === 2 && (
                <>
                  <Question label={t("q.locationStatus", "Do you already have a location for the project?")} required>
                    {LOCATION_STATUS.map((o) => (
                      <Option key={o.value} testId={`f-location-${o.value}`} selected={a.locationStatus === o.value} onClick={() => set("locationStatus", o.value)}>{t(`opt.${o.value}`, o.label)}</Option>
                    ))}
                  </Question>
                  <Question label={t("q.facilityArea", "What is the approximate available floor area?")} required>
                    {FACILITY_AREAS.map((o) => (
                      <Option key={o.value} testId={`f-area-${o.value}`} selected={a.facilityArea === o.value} onClick={() => set("facilityArea", o.value)}>{t(`opt.${o.value}`, o.label)}</Option>
                    ))}
                  </Question>
                </>
              )}

              {step === 3 && (
                <>
                  <Question label={t("q.investmentBand", "What is your estimated investment range? (USD)")} required>
                    {INVESTMENTS_V2.map(([v, k, d]) => (
                      <Option key={v} testId={`f-investment-${v}`} selected={a.investmentBand === v} onClick={() => set("investmentBand", v)}>{t(k, d)}</Option>
                    ))}
                  </Question>
                  <Question label={t("q.equipmentListStatus", "Do you already have an equipment list?")} required>
                    {EQUIP_LIST_V2.map(([v, k, d]) => (
                      <Option key={v} testId={`f-equiplist-${v}`} selected={a.equipmentListStatus === v} onClick={() => set("equipmentListStatus", v)}>{t(k, d)}</Option>
                    ))}
                  </Question>
                </>
              )}

              {step === 4 && (
                <>
                  <div className="grid gap-6 sm:grid-cols-2">
                    <label className="block">
                      <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-titanium/50">
                        {t("q.country", "Project Country")} <span className="text-signal">*</span>
                      </span>
                      <select
                        value={a.country}
                        onChange={(e) => {
                          const c = COUNTRIES.find((x) => x.name === e.target.value);
                          // Project country only SUGGESTS the phone country
                          // (until the visitor picks a phone country or types);
                          // the visitor can always change it independently.
                          setA((p) => ({
                            ...p,
                            country: e.target.value,
                            phoneCountry: !p.phoneTouched && !p.phone && c?.iso2 ? c.iso2 : p.phoneCountry,
                          }));
                        }}
                        className="w-full rounded-none border border-carbon-600 bg-carbon-800 px-4 py-3.5 text-sm text-white outline-none focus:border-signal"
                        data-testid="f-country"
                      >
                        <option value="">{t("q.selectCountry", "Select country")}</option>
                        {COUNTRIES.map((c) => (
                          <option key={c.name} value={c.name}>{c.name}</option>
                        ))}
                      </select>
                    </label>
                    <Input label={t("q.city", "City")} required placeholder={t("q.city", "City")} value={a.city} onChange={(e) => set("city", e.target.value)} data-testid="f-city" />
                    <Input label={t("q.fullName", "Full Name")} required value={a.fullName} onChange={(e) => set("fullName", e.target.value)} data-testid="f-full-name" autoComplete="name" />
                    <Input label={t("q.company", "Company / Gym Name (optional)")} value={a.company} onChange={(e) => set("company", e.target.value)} data-testid="f-company" />
                    <div className="sm:col-span-2">
                      <Input label={t("q.email", "Email Address")} required type="email" value={a.email} onChange={(e) => set("email", e.target.value)} data-testid="f-email" autoComplete="email" />
                    </div>
                    <div className="sm:col-span-2">
                      <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-titanium/50">
                        {t("q.phone", "Phone / WhatsApp")} <span className="text-signal">*</span>
                      </span>
                      <PhoneIntl
                        value={a.phone}
                        country={a.phoneCountry || undefined}
                        onChange={(v) => set("phone", v)}
                        onCountryChange={(c) => set("phoneCountry", c)}
                        onUserCountrySelect={() => set("phoneTouched", true)}
                        invalid={Boolean(a.phone) && !isValidPhoneNumber(a.phone)}
                        inputTestId="f-phone"
                        selectTestId="f-phone-country"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-titanium/50">
                        {t("q.preferredContact", "Preferred Contact Method")} <span className="text-signal">*</span>
                      </span>
                      <div className="grid grid-cols-3 gap-2">
                        {CONTACT_METHODS.map((o) => (
                          <Option key={o.value} testId={`f-contact-${o.value}`} selected={a.preferredContact === o.value} onClick={() => set("preferredContact", o.value)}>{t(`opt.${o.value}`, o.label)}</Option>
                        ))}
                      </div>
                    </div>
                  </div>
                  <label className="mt-8 flex cursor-pointer items-start gap-3" data-testid="f-privacy-consent">
                    <input type="checkbox" checked={a.privacyConsent} onChange={(e) => set("privacyConsent", e.target.checked)} className="mt-1 h-4 w-4 shrink-0 rounded-none border-carbon-600 bg-carbon-800 accent-signal" />
                    <span className="text-sm font-light leading-relaxed text-titanium/70">
                      {t("consent.privacy", "I consent to YANRE FITNESS EUROPE processing my information to contact me about this project.")} <span className="text-signal">*</span>
                    </span>
                  </label>
                  <label className="mt-4 flex cursor-pointer items-start gap-3" data-testid="f-marketing-consent">
                    <input type="checkbox" checked={a.marketingConsent} onChange={(e) => set("marketingConsent", e.target.checked)} className="mt-1 h-4 w-4 shrink-0 rounded-none border-carbon-600 bg-carbon-800 accent-signal" />
                    <span className="text-sm font-light leading-relaxed text-titanium/50">
                      {t("consent.marketing", "I would also like to receive occasional news and offers from YANRE FITNESS EUROPE. (Optional)")}
                    </span>
                  </label>
                </>
              )}
            </div>

            {/* Nav */}
            <div className="flex items-center justify-between border-t border-carbon-700 px-6 py-5 sm:px-10">
              {step > 1 ? (
                <button type="button" onClick={back} className="btn-ghost border-carbon-600 !px-5 !py-3 text-titanium/70 hover:border-titanium/50 hover:text-white" data-testid="form-back-btn">
                  <ArrowLeft className="h-4 w-4 rtl:-scale-x-100" /> {t("form.back", "Back")}
                </button>
              ) : <span />}
              {step < TOTAL ? (
                <button type="button" onClick={next} className="btn-signal group !px-5 !py-3" data-testid="form-next-btn">
                  {t("form.continue", "Continue")} <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 rtl:-scale-x-100 rtl:group-hover:-translate-x-1" />
                </button>
              ) : (
                <button type="button" onClick={submit} disabled={busy} className="btn-signal group !px-6 !py-3 disabled:opacity-50" data-testid="form-submit-btn">
                  {busy ? t("form.sending", "Sending…") : t("form.submit", "Get My Gym Project")}
                  {!busy && <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 rtl:-scale-x-100 rtl:group-hover:-translate-x-1" />}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
